import { useCallback, useEffect, useState } from 'react';
import { Code2, RefreshCw, Save, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { useParams } from 'react-router-dom';
import ErrorDetectConfigEditor from '../components/ErrorDetectConfigEditor';
import { Toast } from '../components/ui';
import { getErrorDetectConfig, setErrorDetectConfig } from '../api/api';
import styles from './HubErrorDetectConfig.module.scss';

function HubErrorDetectConfig() {
  const { serialNumber } = useParams();
  const [configData, setConfigData] = useState('');
  const [savedConfigData, setSavedConfigData] = useState('');
  const [editorMode, setEditorMode] = useState('guided');
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await getErrorDetectConfig(serialNumber);
      const serialized = JSON.stringify(response.data, null, 2);
      setConfigData(serialized);
      setSavedConfigData(serialized);
    } catch {
      setError('Failed to load error detection configuration.');
      setConfigData('');
      setSavedConfigData('');
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleWrite = async () => {
    let parsedData;
    try {
      parsedData = JSON.parse(configData);
    } catch {
      setError('The JSON is invalid. Fix it before saving.');
      setEditorMode('json');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await setErrorDetectConfig(serialNumber, parsedData);
      const serialized = JSON.stringify(parsedData, null, 2);
      setConfigData(serialized);
      setSavedConfigData(serialized);
      setSuccess('Error detection policy saved.');
    } catch {
      setError('Failed to save error detection configuration.');
    } finally {
      setSaving(false);
    }
  };

  const dirty = Boolean(configData && configData !== savedConfigData);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Hub tools</span>
          <h1>Error detection</h1>
          <p>
            Define which hub signals become operational incidents and how each threshold behaves.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={fetchConfig}
            disabled={loading}
          >
            <RefreshCw size={15} className={loading ? styles.spinning : ''} /> Reload
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleWrite}
            disabled={saving || !dirty}
          >
            <Save size={15} /> {saving ? 'Saving…' : dirty ? 'Save policy' : 'Saved'}
          </button>
        </div>
      </header>
      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}

      <section className={styles.policyPanel}>
        <div className={styles.policyHeader}>
          <div>
            <span className={styles.policyIcon}>
              <ShieldAlert size={18} />
            </span>
            <div>
              <strong>Detection policy</strong>
              <small>Changes apply after saving to the hub.</small>
            </div>
          </div>
          <div className={styles.modeSwitcher}>
            <button
              type="button"
              className={editorMode === 'guided' ? styles.modeActive : ''}
              onClick={() => setEditorMode('guided')}
            >
              <SlidersHorizontal size={14} /> Guided
            </button>
            <button
              type="button"
              className={editorMode === 'json' ? styles.modeActive : ''}
              onClick={() => setEditorMode('json')}
            >
              <Code2 size={14} /> JSON
            </button>
          </div>
        </div>
        <div className={styles.editorBody}>
          {loading && !configData ? (
            <div className={styles.loadingState}>Loading detection policy…</div>
          ) : editorMode === 'guided' ? (
            <ErrorDetectConfigEditor
              configData={configData}
              onConfigChange={value => {
                if (!isEditingJson) {
                  setConfigData(value);
                }
              }}
              isEditingJson={isEditingJson}
            />
          ) : (
            <textarea
              value={configData}
              onChange={event => setConfigData(event.target.value)}
              onFocus={() => setIsEditingJson(true)}
              onBlur={() => setIsEditingJson(false)}
              className={styles.jsonEditor}
              spellCheck={false}
            />
          )}
        </div>
      </section>
    </div>
  );
}

export default HubErrorDetectConfig;
